import { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis
} from 'recharts'
import { useDataQuality } from '../hooks/useDataQuality'
import {
  AnomalySeverity,
  AnomalyType,
  Company,
  CompanyQualityData,
  DataQualityAnomaly,
  DataQualityReport,
  ProcessProgress,
  SegmentProcessResult
} from '../types'
import { ProcessButton, ProcessStatus } from './ProcessButton'
import { formatRoiValue } from '../utils/roi-copy'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

// ─── Sidebar module definitions ───────────────────────────────────────────────
type ModuleKey =
  | 'quality'
  | 'roi-methods'
  | 'coverage'
  | 'distributions'
  | 'health-score'
  | 'consistency'

const MODULES: Array<{
  key: ModuleKey
  label: string
  icon: string
  description: string
}> = [
  { key: 'quality',       label: 'Anomalias',              icon: '🔍', description: 'Outliers, anomalias e flags' },
  { key: 'coverage',      label: 'Cobertura de Dados',     icon: '📋', description: 'Campos presentes por empresa' },
  { key: 'health-score',  label: 'Health Score',           icon: '💚', description: 'Confiabilidade por empresa' },
  { key: 'consistency',   label: 'Consistência',           icon: '🔗', description: 'Validações cruzadas entre campos' },
  { key: 'distributions', label: 'Distribuições',          icon: '📊', description: 'Box plots das métricas-chave' },
  { key: 'roi-methods',   label: 'Metodologias ROI',       icon: '🧮', description: 'Comparar métodos de cálculo' },
]

// ─── Shared sub-components ────────────────────────────────────────────────────
const SEVERITY_CONFIG: Record<
  AnomalySeverity,
  { label: string; bg: string; color: string; border: string }
> = {
  critical: {
    label: 'Critico',
    bg: '#fef2f2',
    color: '#991b1b',
    border: '#fecaca'
  },
  warning: {
    label: 'Atenção',
    bg: '#fffbeb',
    color: '#92400e',
    border: '#fde68a'
  },
  info: { label: 'Info', bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' }
}

const ANOMALY_TYPE_LABELS: Record<AnomalyType, string> = {
  roi_outlier_high: 'ROI muito alto',
  roi_outlier_low: 'ROI muito baixo',
  cost_disproportionate: 'Custo desproporcional',
  partial_ga: 'Dados GA parciais',
  zero_orders: 'Zero pedidos',
  zero_cost: 'Sem custo',
  first_month_partial: '1o mês parcial',
  high_discount_suspect: 'Suspeita de desconto',
  no_active_videos: 'Sem vídeos ativos',
  influenced_exceeds_total: 'Influência > total',
  high_influence_rate: 'Taxa influência alta',
  canceled_during_period: 'Cancelado no período',
  no_video_sessions_with_influence: 'Sem sessões c/ vídeo',
  revenue_outlier: 'Receita outlier',
  ticket_medio_anomaly: 'Ticket médio anômalo',
  zero_influenced: 'Zero influenciados'
}

function SeverityBadge({ severity }: { severity: AnomalySeverity }) {
  const cfg = SEVERITY_CONFIG[severity]
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 10,
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`
      }}
    >
      {cfg.label}
    </span>
  )
}

function AnomalyTypeBadge({ type }: { type: AnomalyType }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 10,
        background: '#f3f4f6',
        color: '#374151'
      }}
    >
      {ANOMALY_TYPE_LABELS[type]}
    </span>
  )
}

// ─── Quality module sub-components ────────────────────────────────────────────
function SummaryCards({ report }: { report: DataQualityReport }) {
  const { summary, statistics } = report
  const cards = [
    { label: 'Empresas', value: summary.totalCompanies, color: '#374151' },
    {
      label: 'Anomalias',
      value: summary.companiesWithAnomalies,
      color: summary.companiesWithAnomalies > 0 ? '#dc2626' : '#059669'
    },
    {
      label: 'Criticos',
      value: summary.criticalCount,
      color: summary.criticalCount > 0 ? '#dc2626' : '#9ca3af'
    },
    {
      label: 'Avisos',
      value: summary.warningCount,
      color: summary.warningCount > 0 ? '#d97706' : '#9ca3af'
    },
    {
      label: 'ROI Mediana',
      value: `${formatRoiValue(statistics.roi.median)}x`,
      color: '#7c3aed'
    },
    {
      label: 'ROI IQR',
      value: `${statistics.roi.lowerFence.toFixed(1)} - ${statistics.roi.upperFence.toFixed(1)}`,
      color: '#6b7280'
    }
  ]
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 10,
        marginBottom: 20
      }}
    >
      {cards.map((card, i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '10px 14px'
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 2
            }}
          >
            {card.label}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: card.color }}>
            {card.value}
          </div>
        </div>
      ))}
    </div>
  )
}

function RoiVsCostChart({ companies }: { companies: CompanyQualityData[] }) {
  const data = companies
    .filter(c => c.roi !== null && c.proportionalCost > 0)
    .map(c => ({
      name: c.companyTitle,
      cost: c.proportionalCost,
      roi: c.roi!,
      hasAnomaly: c.anomalyFlags.length > 0
    }))
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}
    >
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#374151' }}>
        ROI vs Custo
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="cost"
            type="number"
            fontSize={11}
            tickFormatter={(v: number) => `R$${v.toFixed(0)}`}
          />
          <YAxis
            dataKey="roi"
            type="number"
            fontSize={11}
            tickFormatter={(v: number) => `${formatRoiValue(v)}x`}
          />
          <RechartsTooltip
            content={({ payload }) => {
              if (!payload?.length) return null
              const d = payload[0].payload
              return (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    padding: 8,
                    fontSize: 12
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{d.name}</div>
                  <div>
                    ROI: {formatRoiValue(d.roi)}x | Custo: {formatCurrency(d.cost)}
                  </div>
                </div>
              )
            }}
          />
          <Scatter
            data={data.filter(d => !d.hasAnomaly)}
            fill="#6366f1"
            fillOpacity={0.6}
          />
          <Scatter
            data={data.filter(d => d.hasAnomaly)}
            fill="#dc2626"
            fillOpacity={0.8}
            shape="diamond"
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )
}

function RoiDistributionChart({
  companies,
  stats
}: {
  companies: CompanyQualityData[]
  stats: DataQualityReport['statistics']['roi']
}) {
  const roiValues = companies.filter(c => c.roi !== null).map(c => c.roi!)
  if (roiValues.length === 0) return null
  const min = Math.floor(Math.min(...roiValues))
  const max = Math.ceil(Math.max(...roiValues))
  const bucketSize = Math.max(1, Math.ceil((max - min) / 15))
  const buckets: { range: string; count: number; isOutlier: boolean }[] = []
  for (let start = min; start <= max; start += bucketSize) {
    const end = start + bucketSize
    const count = roiValues.filter(v => v >= start && v < end).length
    const midpoint = (start + end) / 2
    buckets.push({
      range: `${start.toFixed(0)}-${end.toFixed(0)}x`,
      count,
      isOutlier: midpoint > stats.upperFence || midpoint < stats.lowerFence
    })
  }
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}
    >
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#374151' }}>
        Distribuição de ROI
      </h3>
      <ResponsiveContainer width="100%" height={230}>
        <BarChart
          data={buckets}
          margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="range"
            fontSize={10}
            angle={-30}
            textAnchor="end"
            height={50}
          />
          <YAxis fontSize={11} allowDecimals={false} />
          <RechartsTooltip
            content={({ payload }) => {
              if (!payload?.length) return null
              const d = payload[0].payload
              return (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    padding: 8,
                    fontSize: 12
                  }}
                >
                  <div>
                    {d.range}: {d.count} empresas
                  </div>
                </div>
              )
            }}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {buckets.map((entry, index) => (
              <Cell
                key={index}
                fill={entry.isOutlier ? '#f87171' : '#818cf8'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function AnomalyList({
  anomalies,
  onSelectCompany,
  filterType,
  filterSeverity
}: {
  anomalies: DataQualityAnomaly[]
  onSelectCompany: (k: string) => void
  filterType: AnomalyType | 'all'
  filterSeverity: AnomalySeverity | 'all'
}) {
  const filtered = anomalies.filter(
    a =>
      (filterType === 'all' || a.type === filterType) &&
      (filterSeverity === 'all' || a.severity === filterSeverity)
  )
  if (filtered.length === 0)
    return (
      <div
        style={{
          padding: 20,
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 13
        }}
      >
        Nenhuma anomalia encontrada.
      </div>
    )
  return (
    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
      {filtered.map((a, i) => (
        <div
          key={`${a.companyKey}-${a.type}-${i}`}
          onClick={() => onSelectCompany(a.companyKey)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '10px 12px',
            borderBottom: '1px solid #f3f4f6',
            cursor: 'pointer'
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <SeverityBadge severity={a.severity} />
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginBottom: 2
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                {a.companyTitle}
              </span>
              <AnomalyTypeBadge type={a.type} />
              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                {a.planType}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {a.description}
            </div>
          </div>
          <span style={{ color: '#9ca3af' }}>›</span>
        </div>
      ))}
    </div>
  )
}

function DrillDownPanel({
  company,
  anomalies,
  stats,
  onClose
}: {
  company: CompanyQualityData
  anomalies: DataQualityAnomaly[]
  stats: DataQualityReport['statistics']
  onClose: () => void
}) {
  const ca = anomalies.filter(a => a.companyKey === company.companyKey)
  const metrics = [
    { label: 'Total Pedidos', value: company.totalOrders, stat: null },
    {
      label: 'Receita Total',
      value: formatCurrency(company.totalRevenue),
      stat: `Med: ${formatCurrency(stats.revenue.median)}`
    },
    {
      label: 'Pedidos Influenciados',
      value: company.influencedOrders,
      stat: null
    },
    {
      label: 'Rec. Influenciada',
      value: formatCurrency(company.influencedRevenue),
      stat: null
    },
    {
      label: 'ROI',
      value: company.roi !== null ? `${formatRoiValue(company.roi)}x` : '—',
      stat: `Med: ${formatRoiValue(stats.roi.median)}x | [${formatRoiValue(stats.roi.lowerFence)}, ${formatRoiValue(stats.roi.upperFence)}]`
    },
    {
      label: 'Custo Mensal',
      value: formatCurrency(company.proportionalCost),
      stat: `Med: ${formatCurrency(stats.cost.median)}`
    },
    {
      label: 'Conversão',
      value: `${company.conversionRate.toFixed(1)}%`,
      stat: `Med: ${stats.conversionRate.median.toFixed(1)}%`
    },
    {
      label: '% Influenciada',
      value: `${company.influencedPercent.toFixed(1)}%`,
      stat: `Med: ${stats.influencedPercent.median.toFixed(1)}%`
    },
    {
      label: 'Dias GA',
      value:
        company.gaActiveDays !== undefined
          ? `${company.gaActiveDays}`
          : 'Completo',
      stat: null
    },
    { label: 'Plano', value: company.planType, stat: null },
    { label: 'Status', value: company.subscriptionStatus, stat: null },
    {
      label: '1o Mês Parcial',
      value: company.isFirstMonthPartial ? 'Sim' : 'Não',
      stat: null
    },
    {
      label: 'Assinatura',
      value: company.subscriptionCreatedAt
        ? new Date(company.subscriptionCreatedAt).toLocaleDateString('pt-BR')
        : '—',
      stat: null
    }
  ]
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 480,
        background: '#fff',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
        zIndex: 1000,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: '#fff',
          zIndex: 1
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>{company.companyTitle}</h3>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {company.companyKey}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 13,
            color: '#6b7280'
          }}
        >
          Fechar
        </button>
      </div>
      <div style={{ padding: 20, flex: 1 }}>
        {ca.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h4
              style={{
                margin: '0 0 8px',
                fontSize: 13,
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Anomalias ({ca.length})
            </h4>
            {ca.map((a, i) => (
              <div
                key={i}
                style={{
                  padding: '8px 12px',
                  marginBottom: 6,
                  background: SEVERITY_CONFIG[a.severity].bg,
                  border: `1px solid ${SEVERITY_CONFIG[a.severity].border}`,
                  borderRadius: 6,
                  fontSize: 12
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    marginBottom: 4
                  }}
                >
                  <SeverityBadge severity={a.severity} />
                  <AnomalyTypeBadge type={a.type} />
                </div>
                <div style={{ color: '#374151' }}>{a.description}</div>
                {a.details && Object.keys(a.details).length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#6b7280' }}>
                    {Object.entries(a.details).map(([key, val]) => (
                      <div key={key}>
                        <span style={{ fontWeight: 500 }}>{key}:</span>{' '}
                        {typeof val === 'number'
                          ? key.toLowerCase().includes('revenue') ||
                            key.toLowerCase().includes('cost') ||
                            key.toLowerCase().includes('median')
                            ? formatCurrency(val)
                            : val.toFixed(2)
                          : String(val ?? '—')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <h4
          style={{
            margin: '0 0 8px',
            fontSize: 13,
            color: '#374151',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          Métricas
        </h4>
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}
        >
          {metrics.map((m, i) => (
            <div
              key={i}
              style={{
                padding: '8px 10px',
                background: '#f9fafb',
                borderRadius: 6,
                border: '1px solid #f3f4f6'
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: '#6b7280',
                  textTransform: 'uppercase'
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#111827',
                  marginTop: 2
                }}
              >
                {m.value}
              </div>
              {m.stat && (
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                  {m.stat}
                </div>
              )}
            </div>
          ))}
        </div>

        {(company.sessionsWithVideo > 0 ||
          company.sessionsWithoutVideo > 0) && (
          <div style={{ marginTop: 20 }}>
            <h4
              style={{
                margin: '0 0 8px',
                fontSize: 13,
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Engajamento (GA4)
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12
                }}
              >
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th
                      style={{
                        padding: '6px 8px',
                        textAlign: 'left',
                        fontSize: 11,
                        color: '#6b7280'
                      }}
                    ></th>
                    <th
                      style={{
                        padding: '6px 8px',
                        textAlign: 'right',
                        fontSize: 11,
                        color: '#6b7280'
                      }}
                    >
                      Com Vídeo
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        textAlign: 'right',
                        fontSize: 11,
                        color: '#6b7280'
                      }}
                    >
                      Sem Vídeo
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        textAlign: 'right',
                        fontSize: 11,
                        color: '#6b7280'
                      }}
                    >
                      Delta
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: 'Sessões',
                      wv: company.sessionsWithVideo,
                      wov: company.sessionsWithoutVideo,
                      fmt: (v: number) => v.toLocaleString()
                    },
                    {
                      label: 'Usuários',
                      wv: company.usersWithVideo,
                      wov: company.usersWithoutVideo,
                      fmt: (v: number) => v.toLocaleString()
                    },
                    {
                      label: 'Bounce Rate',
                      wv: company.bounceRateWithVideo,
                      wov: company.bounceRateWithoutVideo,
                      fmt: (v: number) => `${v.toFixed(1)}%`
                    },
                    {
                      label: 'Duração Sessão',
                      wv: company.avgSessionDurationWithVideo,
                      wov: company.avgSessionDurationWithoutVideo,
                      fmt: (v: number) => `${v.toFixed(0)}s`
                    },
                    {
                      label: 'Págs/Sessão',
                      wv: company.pagesPerSessionWithVideo,
                      wov: company.pagesPerSessionWithoutVideo,
                      fmt: (v: number) => v.toFixed(1)
                    }
                  ].map((row, i) => {
                    const delta =
                      row.wov > 0 ? ((row.wv - row.wov) / row.wov) * 100 : null
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td
                          style={{
                            padding: '6px 8px',
                            fontWeight: 500,
                            color: '#374151'
                          }}
                        >
                          {row.label}
                        </td>
                        <td
                          style={{
                            padding: '6px 8px',
                            textAlign: 'right',
                            fontWeight: 600
                          }}
                        >
                          {row.fmt(row.wv)}
                        </td>
                        <td
                          style={{
                            padding: '6px 8px',
                            textAlign: 'right',
                            color: '#6b7280'
                          }}
                        >
                          {row.fmt(row.wov)}
                        </td>
                        <td
                          style={{
                            padding: '6px 8px',
                            textAlign: 'right',
                            color:
                              delta !== null
                                ? delta > 0
                                  ? '#059669'
                                  : delta < 0
                                    ? '#dc2626'
                                    : '#6b7280'
                                : '#9ca3af',
                            fontWeight: 500
                          }}
                        >
                          {delta !== null
                            ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`
                            : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CompanyQualityTable({
  companies,
  onSelectCompany,
  sortField,
  sortDir,
  onSort
}: {
  companies: CompanyQualityData[]
  onSelectCompany: (k: string) => void
  sortField: string
  sortDir: 'asc' | 'desc'
  onSort: (f: string) => void
}) {
  const SortHeader = ({ field, label }: { field: string; label: string }) => (
    <th
      onClick={() => onSort(field)}
      style={{
        padding: '8px 10px',
        textAlign: 'left',
        fontSize: 11,
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        borderBottom: '2px solid #e5e7eb'
      }}
    >
      {label} {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  )
  return (
    <div
      style={{
        overflowX: 'auto',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8
      }}
    >
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <SortHeader field="companyTitle" label="Empresa" />
            <SortHeader field="planType" label="Plano" />
            <SortHeader field="totalOrders" label="Pedidos" />
            <SortHeader field="totalRevenue" label="Receita" />
            <SortHeader field="roi" label="ROI" />
            <SortHeader field="proportionalCost" label="Custo" />
            <SortHeader field="conversionRate" label="Conv %" />
            <SortHeader field="gaActiveDays" label="Dias GA" />
            <th
              style={{
                padding: '8px 10px',
                textAlign: 'left',
                fontSize: 11,
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                borderBottom: '2px solid #e5e7eb'
              }}
            >
              Flags
            </th>
          </tr>
        </thead>
        <tbody>
          {companies.map(c => (
            <tr
              key={c.companyKey}
              onClick={() => onSelectCompany(c.companyKey)}
              style={{ cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
              onMouseLeave={e =>
                (e.currentTarget.style.background = 'transparent')
              }
            >
              <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                {c.companyTitle}
              </td>
              <td style={{ padding: '8px 10px', color: '#6b7280' }}>
                {c.planType}
              </td>
              <td style={{ padding: '8px 10px' }}>{c.totalOrders}</td>
              <td style={{ padding: '8px 10px' }}>
                {formatCurrency(c.totalRevenue)}
              </td>
              <td
                style={{
                  padding: '8px 10px',
                  fontWeight: 600,
                  color:
                    c.roi !== null
                      ? c.roi >= 3
                        ? '#059669'
                        : c.roi >= 1
                          ? '#1e40af'
                          : '#dc2626'
                      : '#9ca3af'
                }}
              >
                {c.roi !== null ? `${formatRoiValue(c.roi)}x` : '—'}
              </td>
              <td style={{ padding: '8px 10px' }}>
                {formatCurrency(c.proportionalCost)}
              </td>
              <td style={{ padding: '8px 10px' }}>
                {c.conversionRate.toFixed(1)}%
              </td>
              <td
                style={{
                  padding: '8px 10px',
                  color: c.gaActiveDays !== undefined ? '#d97706' : '#6b7280'
                }}
              >
                {c.gaActiveDays ?? '—'}
              </td>
              <td style={{ padding: '8px 10px' }}>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {c.anomalyFlags.map((f, i) => (
                    <AnomalyTypeBadge key={i} type={f as AnomalyType} />
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Quality Module ───────────────────────────────────────────────────────────
function QualityModule({
  report,
  onSelectCompany
}: {
  report: DataQualityReport
  onSelectCompany: (k: string) => void
}) {
  const [filterType, setFilterType] = useState<AnomalyType | 'all'>('all')
  const [filterSeverity, setFilterSeverity] = useState<AnomalySeverity | 'all'>(
    'all'
  )
  const [sortField, setSortField] = useState('roi')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [activeTab, setActiveTab] = useState<'anomalies' | 'charts' | 'table'>(
    'anomalies'
  )

  const handleSort = (field: string) => {
    if (sortField === field)
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    else {
      setSortField(field)
      setSortDir('desc')
    }
  }
  const sortedCompanies = useMemo(() => {
    const list = [...report.companies]
    list.sort((a, b) => {
      let aVal: any = (a as any)[sortField]
      let bVal: any = (b as any)[sortField]
      if (aVal === null || aVal === undefined) aVal = -Infinity
      if (bVal === null || bVal === undefined) bVal = -Infinity
      if (typeof aVal === 'string')
        return sortDir === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    })
    return list
  }, [report, sortField, sortDir])

  return (
    <>
      <SummaryCards report={report} />
      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 16,
          borderBottom: '2px solid #e5e7eb'
        }}
      >
        {(
          [
            ['anomalies', `Anomalias (${report.anomalies.length})`],
            ['charts', 'Gráficos'],
            ['table', `Empresas (${report.companies.length})`]
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              background: 'none',
              color: activeTab === key ? '#7c3aed' : '#6b7280',
              borderBottom:
                activeTab === key
                  ? '2px solid #7c3aed'
                  : '2px solid transparent',
              marginBottom: -2
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {activeTab === 'anomalies' && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 12,
              flexWrap: 'wrap'
            }}
          >
            <select
              value={filterSeverity}
              onChange={e => setFilterSeverity(e.target.value as any)}
              style={{
                padding: '4px 8px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #d1d5db'
              }}
            >
              <option value="all">Todas severidades</option>
              <option value="critical">Critico</option>
              <option value="warning">Atenção</option>
              <option value="info">Info</option>
            </select>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value as any)}
              style={{
                padding: '4px 8px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #d1d5db'
              }}
            >
              <option value="all">Todos os tipos</option>
              {Object.entries(ANOMALY_TYPE_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <AnomalyList
            anomalies={report.anomalies}
            onSelectCompany={onSelectCompany}
            filterType={filterType}
            filterSeverity={filterSeverity}
          />
        </div>
      )}
      {activeTab === 'charts' && (
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
        >
          <RoiVsCostChart companies={report.companies} />
          <RoiDistributionChart
            companies={report.companies}
            stats={report.statistics.roi}
          />
        </div>
      )}
      {activeTab === 'table' && (
        <CompanyQualityTable
          companies={sortedCompanies}
          onSelectCompany={onSelectCompany}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />
      )}
    </>
  )
}

// ─── ROI Methods Module ──────────────────────────────────────────────────────

interface RoiMethodResult {
  label: string
  value: string
  category: string
  categoryColor: string
  formula: string
  pros: string[]
  cons: string[]
  useCase: string
  isCurrent?: boolean
}

function percentile(arr: number[], p: number): number {
  const sorted = [...arr].sort((a, b) => a - b)
  const idx = (p / 100) * (sorted.length - 1)
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sorted[lo]
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo)
}

function RoiMethodsModule({ companies, backendAvgRoi }: { companies: CompanyQualityData[]; backendAvgRoi: number | null }) {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)

  const methods = useMemo<RoiMethodResult[]>(() => {
    const valid = companies.filter(
      c => c.roi !== null && c.totalOrders > 0 && c.proportionalCost > 0
    )
    const rois = valid.map(c => c.roi!)

    if (rois.length === 0) return []

    const totalCost = valid.reduce((s, c) => s + c.proportionalCost, 0)
    const influencedRevenue = valid.reduce((s, c) => s + c.influencedRevenue, 0)

    // 1. Média simples
    const simpleAvg = rois.reduce((s, v) => s + v, 0) / rois.length

    // 2. ROI ponderado (gold standard) — baseado em receita influenciada
    const weightedRoi =
      totalCost > 0 ? (influencedRevenue - totalCost) / totalCost : 0

    // 3. Média ponderada por custo
    const weightedByCost =
      valid.reduce((s, c) => s + c.roi! * c.proportionalCost, 0) / totalCost

    // 4. Mediana
    const sorted = [...rois].sort((a, b) => a - b)
    const median = percentile(rois, 50)

    // 5. Percentis
    const p25 = percentile(rois, 25)
    const p75 = percentile(rois, 75)
    const p90 = percentile(rois, 90)

    // 6. Média truncada (10%)
    const trimPct = 0.1
    const trimCount = Math.floor(rois.length * trimPct)
    const trimmed = sorted.slice(trimCount, sorted.length - trimCount)
    const trimmedMean =
      trimmed.length > 0
        ? trimmed.reduce((s, v) => s + v, 0) / trimmed.length
        : simpleAvg

    // 7. Winsorized mean
    const wLow = sorted[trimCount] ?? sorted[0]
    const wHigh =
      sorted[sorted.length - 1 - trimCount] ?? sorted[sorted.length - 1]
    const winsorized = sorted.map(v => Math.max(wLow, Math.min(wHigh, v)))
    const winsorizedMean =
      winsorized.reduce((s, v) => s + v, 0) / winsorized.length

    // 8. % ROI positivo
    const positiveCount = rois.filter(r => r > 2).length
    const positivePct = (positiveCount / rois.length) * 100

    // 9. Mediana ajustada (-20%)
    const medianAdjusted = median * 0.8

    // 11. ROI mínimo garantido (P10)
    const p10 = percentile(rois, 10)

    // 13. ROI por plano (segmentado)
    const byPlan = new Map<string, number[]>()
    valid.forEach(c => {
      const arr = byPlan.get(c.planType) || []
      arr.push(c.roi!)
      byPlan.set(c.planType, arr)
    })
    const planSummary = [...byPlan.entries()]
      .map(
        ([plan, vals]) =>
          `${plan}: ${formatRoiValue(vals.reduce((s, v) => s + v, 0) / vals.length)}x`
      )
      .join(' | ')

    return [
      {
        label: 'Média Simples',
        value: `${formatRoiValue(simpleAvg)}x`,
        category: 'Clássico',
        categoryColor: '#3b82f6',
        formula:
          '(1/N) × Σ ROI_i — onde ROI_i = (ReceitaInfluenciada - Custo) / Custo',
        pros: [
          'Soma o ROI de cada empresa e divide pelo total de empresas',
          'Cada empresa vale igual — uma que paga R$50/mês tem o mesmo peso que uma que paga R$2.000/mês'
        ],
        cons: [
          'Se uma empresa pequena tem ROI de 400x, ela puxa a média pra cima como se fosse uma empresa grande',
          'Basta 1 outlier pra distorcer o número inteiro'
        ],
        useCase:
          'Simples de entender, mas perigosa pra tomar decisão — trata todo mundo igual independente do tamanho'
      },
      {
        label: 'ROI Agregado',
        value: `${formatRoiValue(weightedRoi)}x`,
        category: 'Clássico',
        categoryColor: '#3b82f6',
        formula: '(Σ ReceitaInfluenciada - Σ Custo) / Σ Custo',
        pros: [
          'Junta toda a receita influenciada de todas as empresas e todo o custo, e faz uma conta só',
          'É como se fosse "uma empresa gigante" — o número representa o resultado real da Widde como negócio'
        ],
        cons: [
          'Empresas maiores naturalmente dominam porque contribuem mais receita e custo no bolo',
          'Se um cliente grande vai mal, puxa o número inteiro pra baixo mesmo que os outros 100 vão bem'
        ],
        useCase:
          'O ROI "real" — quanto a Widde retorna no total para os clientes. Ideal para board, financeiro e investidores'
      },
      {
        label: 'Ponderada por Custo',
        value: `${formatRoiValue(weightedByCost)}x`,
        category: 'Clássico',
        categoryColor: '#3b82f6',
        formula: 'Σ(ROI_i × Custo_i) / Σ Custo_i',
        pros: [
          'Calcula o ROI de cada empresa individualmente, mas dá mais peso pra quem paga mais',
          'Quem paga R$2.000 tem 10x mais influência no resultado que quem paga R$200'
        ],
        cons: [
          'Matematicamente é idêntico ao ROI Agregado quando o ROI é (receita - custo) / custo — por isso os dois valores são iguais',
          'Clientes baratos com ROI excelente ficam praticamente invisíveis no cálculo'
        ],
        useCase:
          'Na prática dá o mesmo resultado que o ROI Agregado. Útil se mudar a fórmula base de ROI no futuro'
      },
      {
        label: 'Mediana (P50)',
        value: `${formatRoiValue(median)}x`,
        category: 'Robusto',
        categoryColor: '#059669',
        formula: 'Ordena todos os ROIs e pega o valor do meio',
        pros: [
          'Se você tem 100 empresas, pega o ROI da 50a — metade está acima, metade abaixo',
          'Não importa se tem uma empresa com ROI 1000x — a mediana não se mexe',
          'Representa "o cliente típico" da Widde'
        ],
        cons: [
          'Não mostra o tamanho financeiro — uma empresa com ROI 5x que fatura R$1M e outra com ROI 5x que fatura R$1K valem igual',
          'Se a maioria dos clientes grandes vai mal, a mediana não vai mostrar isso'
        ],
        useCase:
          'Melhor número para comunicar para Marketing, Vendas e CS: "o cliente típico tem Xx de retorno"'
      },
      {
        label: 'Percentil P25 (Conservador)',
        value: `${formatRoiValue(p25)}x`,
        category: 'Robusto',
        categoryColor: '#059669',
        formula: 'O ROI abaixo do qual estão apenas 25% dos clientes',
        pros: [
          '75% dos clientes estão acima desse valor — é o "pior cenário típico"',
          `Muito seguro pra fazer promessa: "pelo menos ${formatRoiValue(p25)}x pra 3 em cada 4 clientes"`
        ],
        cons: [
          'Pode ser pessimista demais — mostra o quartil mais fraco como se fosse o padrão',
          'Se o P25 é negativo, fica difícil usar como argumento de venda'
        ],
        useCase:
          'Vendas conservadoras e contratos com SLA de resultado. Seguro pra prometer sem risco de frustrar'
      },
      {
        label: 'Percentil P75',
        value: `${formatRoiValue(p75)}x`,
        category: 'Robusto',
        categoryColor: '#059669',
        formula: 'O ROI que apenas os top 25% dos clientes atingem',
        pros: [
          `Mostra o que acontece quando o cliente usa bem — "os melhores 25% chegam a ${formatRoiValue(p75)}x"`,
          'Bom pra upsell e case studies realistas'
        ],
        cons: [
          'Não representa a maioria — 75% dos clientes estão abaixo desse valor',
          'Usar como expectativa geral é enganoso'
        ],
        useCase:
          'Mostrar potencial real: "clientes que aproveitam bem a ferramenta atingem Xx". Bom pra pitch de valor'
      },
      {
        label: 'Percentil P90',
        value: `${formatRoiValue(p90)}x`,
        category: 'Robusto',
        categoryColor: '#059669',
        formula: 'Apenas 10% dos clientes chegam nesse ROI ou acima',
        pros: [
          'Mostra o melhor cenário real — não é inventado, são clientes reais',
          'Ótimo pra case study: "nosso melhor resultado foi Xx"'
        ],
        cons: [
          '90% dos clientes estão abaixo — usar como expectativa é irresponsável',
          'Pode ser inflado por um único outlier'
        ],
        useCase:
          'Marketing aspiracional e case studies. Nunca usar como promessa — é o topo da curva, não a média'
      },
      {
        label: 'Média Truncada (10%)',
        value: `${formatRoiValue(trimmedMean)}x`,
        category: 'Robusto',
        categoryColor: '#059669',
        formula: `Remove os ${trimCount} melhores e ${trimCount} piores, e calcula a média dos que sobram`,
        pros: [
          'Joga fora os extremos (os ROIs absurdamente altos e os muito negativos) e faz a média do "miolo"',
          'Meio termo entre a média simples (que distorce com outliers) e a mediana (que ignora tudo)'
        ],
        cons: [
          'A escolha de 10% é arbitrária — por que não 5% ou 15%?',
          'Pode jogar fora dados que são legítimos, não apenas outliers'
        ],
        useCase:
          'Quando a média simples está distorcida por outliers mas a mediana perde informação demais'
      },
      {
        label: 'Winsorized Mean',
        value: `${formatRoiValue(winsorizedMean)}x`,
        category: 'Robusto',
        categoryColor: '#059669',
        formula: `Ao invés de remover outliers, limita eles: ROI abaixo de ${formatRoiValue(wLow)}x vira ${formatRoiValue(wLow)}x, acima de ${formatRoiValue(wHigh)}x vira ${formatRoiValue(wHigh)}x`,
        pros: [
          'Diferente da truncada, não joga ninguém fora — apenas "tampa" os extremos',
          `Uma empresa com ROI 500x entra na conta como ${formatRoiValue(wHigh)}x, não como 500x`,
          'Mantém o tamanho da amostra intacto'
        ],
        cons: [
          `Distorce os valores reais nas pontas — a empresa com 500x "vira" ${formatRoiValue(wHigh)}x`,
          'O ponto de corte ainda é arbitrário (P10/P90)'
        ],
        useCase:
          'Com poucos clientes, perder dados é pior do que limitar extremos. Melhor que truncar quando a amostra é pequena'
      },
      {
        label: '% ROI > 2x',
        value: `${positivePct.toFixed(0)}%`,
        category: 'Distribucional',
        categoryColor: '#7c3aed',
        formula: `${positiveCount} de ${rois.length} clientes com ROI acima de 2x`,
        pros: [
          `O mais fácil de comunicar: "${positivePct.toFixed(0)}% dos clientes pelo menos dobram o investimento"`,
          'Qualquer pessoa entende sem explicação técnica',
          'O threshold de 2x garante que não é um retorno desprezível'
        ],
        cons: [
          'Não diferencia quem tem 3x de quem tem 300x — todo mundo que passa de 2x conta igual',
          'Não diz nada sobre o quanto acima de 2x os clientes estão'
        ],
        useCase:
          'CS e confiança: "X em cada 10 clientes nossos pelo menos dobram o investimento na Widde"'
      },
      {
        label: 'Mediana Ajustada (-20%)',
        value: `${formatRoiValue(medianAdjusted)}x`,
        category: 'Conservador',
        categoryColor: '#d97706',
        formula: `Mediana (${formatRoiValue(median)}x) com desconto de 20% = ${formatRoiValue(medianAdjusted)}x`,
        pros: [
          'Pega o cliente típico (mediana) e aplica um desconto de segurança',
          `Se a mediana diz ${formatRoiValue(median)}x, você promete ${formatRoiValue(medianAdjusted)}x — margem pra erro`,
          'Combina robustez da mediana com conservadorismo'
        ],
        cons: [
          'O desconto de 20% é subjetivo — não tem base estatística, é uma decisão de negócio',
          'Pode subestimar se os dados já são conservadores'
        ],
        useCase:
          'Melhor equilíbrio entre promessa e realidade. Faz sentido pra comunicação externa com margem de segurança'
      },
      {
        label: 'ROI Mínimo Garantido (P10)',
        value: `${formatRoiValue(p10)}x`,
        category: 'Conservador',
        categoryColor: '#d97706',
        formula: `Apenas 10% dos clientes estão abaixo de ${formatRoiValue(p10)}x — 90% estão acima`,
        pros: [
          `Permite dizer: "9 em cada 10 clientes têm pelo menos ${formatRoiValue(p10)}x de retorno"`,
          'Extremamente seguro — quase ninguém fica abaixo desse valor'
        ],
        cons: [
          'Se o P10 é negativo ou muito baixo, não funciona como argumento',
          'É o "pior cenário quase garantido", não o cenário típico'
        ],
        useCase:
          'Garantia de resultado em vendas: "posso afirmar com 90% de confiança que o retorno é de pelo menos Xx"'
      },
      {
        label: 'ROI Bayesiano (Split-SD Shrinkage)',
        value: backendAvgRoi !== null ? `${formatRoiValue(backendAvgRoi)}x` : '\u2014',
        category: 'Avançado',
        categoryColor: '#dc2626',
        formula: `Calculado no backend sobre dados limpos (apos health checks). Mesmo valor do card de ROI no Dashboard.`,
        pros: [
          'Dados limpos: health checks removem empresas problematicas ANTES do calculo',
          'Split-SD: desvio padrao separado para ROIs acima e abaixo da mediana',
          'Valor identico ao Dashboard — fonte unica de verdade no backend'
        ],
        cons: [
          'Comprime valores extremos em direcao a mediana',
          'Pode subestimar se houver muitas empresas com ROI legitimamente alto'
        ],
        useCase:
          'Metodo oficial usado no Dashboard e relatorios. Aplicado sobre dados ja filtrados pelos health checks.',
        isCurrent: true
      },
      {
        label: 'ROI Segmentado por Plano',
        value: planSummary || '—',
        category: 'Avançado',
        categoryColor: '#dc2626',
        formula: 'Calcula a média de ROI separadamente para cada tipo de plano',
        pros: [
          'Mostra onde a Widde funciona melhor — talvez o ROI do PRO seja ótimo mas do STANDARD não',
          'Elimina viés de misturar planos com preços e perfis muito diferentes',
          'Ajuda a entender se o problema é o produto ou o perfil do cliente'
        ],
        cons: [
          'Dá vários números ao invés de um — mais difícil de comunicar',
          'Com poucos clientes por plano, cada número individual fica instável'
        ],
        useCase:
          'Decisão interna: pricing, features por plano, entender qual segmento funciona melhor'
      }
    ]
  }, [companies])

  if (methods.length === 0) {
    return (
      <div style={{ padding: 20, color: '#6b7280' }}>
        Nenhuma empresa com ROI calculado disponível.
      </div>
    )
  }

  const categories = [
    'Clássico',
    'Robusto',
    'Distribucional',
    'Conservador',
    'Avançado'
  ]

  return (
    <div>
      <h3 style={{ margin: '0 0 4px 0', fontSize: 16 }}>
        Metodologias de Cálculo de ROI
      </h3>
      <p style={{ margin: '0 0 6px 0', fontSize: 13, color: '#6b7280' }}>
        Compare diferentes abordagens para calcular o ROI agregado. Passe o
        mouse sobre cada card para ver detalhes.
      </p>
      <p
        style={{
          margin: '0 0 20px 0',
          fontSize: 12,
          color: '#9ca3af',
          fontStyle: 'italic'
        }}
      >
        Base de todos os cálculos: ROI por empresa = (Receita Influenciada -
        Custo) / Custo. Cada método aplica um peso ou tratamento diferente sobre
        essa base.
      </p>

      {categories.map(cat => {
        const catMethods = methods.filter(m => m.category === cat)
        if (catMethods.length === 0) return null
        return (
          <div key={cat} style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: catMethods[0].categoryColor,
                marginBottom: 8,
                paddingBottom: 4,
                borderBottom: `2px solid ${catMethods[0].categoryColor}20`
              }}
            >
              {cat}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 12
              }}
            >
              {catMethods.map(method => (
                <div
                  key={method.label}
                  onMouseEnter={() => setHoveredCard(method.label)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    position: 'relative',
                    border: method.isCurrent
                      ? '2px solid #059669'
                      : '1px solid #e5e7eb',
                    borderRadius: 10,
                    padding: method.isCurrent ? '13px 15px' : '14px 16px',
                    background: method.isCurrent ? '#f0fdf4' : '#fff',
                    transition: 'all 0.15s',
                    boxShadow:
                      hoveredCard === method.label
                        ? '0 4px 12px rgba(0,0,0,0.08)'
                        : method.isCurrent
                          ? '0 0 0 3px rgba(5,150,105,0.1)'
                          : 'none',
                    borderColor:
                      hoveredCard === method.label
                        ? method.categoryColor
                        : method.isCurrent
                          ? '#059669'
                          : '#e5e7eb',
                    cursor: 'default'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 6
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#374151'
                        }}
                      >
                        {method.label}
                      </span>
                      {method.isCurrent && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: '#059669',
                            color: '#fff',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}
                        >
                          Atual
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: `${method.categoryColor}15`,
                        color: method.categoryColor
                      }}
                    >
                      {method.category}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: method.categoryColor,
                      marginBottom: 6,
                      lineHeight: 1.2,
                      wordBreak:
                        method.value.length > 20 ? 'break-all' : 'normal',
                      ...(method.value.length > 20
                        ? { fontSize: 13, fontWeight: 600 }
                        : {})
                    }}
                  >
                    {method.value}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#9ca3af',
                      fontFamily: 'monospace',
                      marginBottom: 8,
                      lineHeight: 1.4
                    }}
                  >
                    {method.formula}
                  </div>

                  {/* Tooltip on hover */}
                  {hoveredCard === method.label && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        marginTop: 4,
                        padding: 14,
                        background: '#1f2937',
                        color: '#f3f4f6',
                        borderRadius: 8,
                        fontSize: 12,
                        lineHeight: 1.5,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        minWidth: 280
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 8,
                          color: '#fff'
                        }}
                      >
                        {method.label}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            color: '#34d399',
                            marginBottom: 2
                          }}
                        >
                          Prós:
                        </div>
                        {method.pros.map((p, i) => (
                          <div key={i} style={{ paddingLeft: 8 }}>
                            + {p}
                          </div>
                        ))}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            color: '#f87171',
                            marginBottom: 2
                          }}
                        >
                          Contras:
                        </div>
                        {method.cons.map((c, i) => (
                          <div key={i} style={{ paddingLeft: 8 }}>
                            - {c}
                          </div>
                        ))}
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            color: '#93c5fd',
                            marginBottom: 2
                          }}
                        >
                          Quando usar:
                        </div>
                        <div style={{ paddingLeft: 8 }}>{method.useCase}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── MODULE: Cobertura de Dados ───────────────────────────────────────────────

const COVERAGE_FIELDS: Array<{
  key: keyof CompanyQualityData | 'engagementMetrics'
  label: string
  check: (c: CompanyQualityData) => 'ok' | 'warn' | 'missing'
  hint: string
}> = [
  {
    key: 'roi',
    label: 'ROI',
    check: c => c.roi !== null ? 'ok' : 'missing',
    hint: 'ROI não calculado — custo ou receita influenciada zerados',
  },
  {
    key: 'roiAdjusted',
    label: 'ROI Ajustado',
    check: c => c.roiAdjusted !== null && c.roiAdjusted !== undefined ? 'ok' : 'warn',
    hint: 'ROI bayesiano não disponível',
  },
  {
    key: 'gaActiveDays',
    label: 'Dias Ativos GA',
    check: c => (c.gaActiveDays ?? 0) >= 20 ? 'ok' : (c.gaActiveDays ?? 0) > 0 ? 'warn' : 'missing',
    hint: 'Menos de 20 dias ativos no GA pode comprometer médias de sessão',
  },
  {
    key: 'effectiveStartDate',
    label: 'Data Início',
    check: c => c.effectiveStartDate ? 'ok' : 'warn',
    hint: 'Data de início efetiva ausente — atribuição pode estar errada',
  },
  {
    key: 'isFirstMonthPartial',
    label: 'Mês Parcial',
    check: c => c.isFirstMonthPartial ? 'warn' : 'ok',
    hint: 'Primeiro mês incompleto — métricas de período refletem menos dias',
  },
  {
    key: 'activeVideos',
    label: 'Vídeos Ativos',
    check: c => c.activeVideos > 0 ? 'ok' : 'missing',
    hint: 'Nenhum vídeo ativo — influência não pode ser atribuída ao produto',
  },
  {
    key: 'engagementMetrics',
    label: 'Métricas de Sessão',
    check: c => c.sessionsWithVideo > 0 ? 'ok' : c.activeVideos > 0 ? 'warn' : 'missing',
    hint: 'Sem sessões com vídeo registradas no GA',
  },
  {
    key: 'influencedPercent',
    label: '% Influenciada',
    check: c => c.influencedPercent > 0 ? 'ok' : 'missing',
    hint: 'Nenhum pedido influenciado registrado',
  },
]

function CoverageCell({ status, hint }: { status: 'ok' | 'warn' | 'missing'; hint: string }) {
  const cfg = {
    ok:      { bg: '#f0fdf4', color: '#16a34a', symbol: '✓' },
    warn:    { bg: '#fffbeb', color: '#d97706', symbol: '!' },
    missing: { bg: '#fef2f2', color: '#dc2626', symbol: '✗' },
  }[status]
  return (
    <td title={hint} style={{ padding: '7px 10px', textAlign: 'center', cursor: 'default' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 4,
        background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700,
      }}>{cfg.symbol}</span>
    </td>
  )
}

function CoverageModule({ companies }: { companies: CompanyQualityData[] }) {
  const [sortBy, setSortBy] = useState<'name' | 'score'>('score')

  const rows = useMemo(() => companies.map(c => {
    const statuses = COVERAGE_FIELDS.map(f => f.check(c))
    const score = statuses.filter(s => s === 'ok').length
    const warns = statuses.filter(s => s === 'warn').length
    return { c, statuses, score, warns }
  }), [companies])

  const sorted = [...rows].sort((a, b) =>
    sortBy === 'score' ? a.score - b.score : a.c.companyTitle.localeCompare(b.c.companyTitle)
  )

  const totals = COVERAGE_FIELDS.map((_, i) => ({
    ok: rows.filter(r => r.statuses[i] === 'ok').length,
    warn: rows.filter(r => r.statuses[i] === 'warn').length,
    missing: rows.filter(r => r.statuses[i] === 'missing').length,
  }))

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Cobertura de Dados</div>
          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>
            Quais campos estão presentes, incompletos ou ausentes por empresa
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, background: 'var(--ink-100)', borderRadius: 999, padding: 3 }}>
          {(['score', 'name'] as const).map(k => (
            <button key={k} onClick={() => setSortBy(k)} style={{
              padding: '4px 12px', borderRadius: 999, border: 'none',
              background: sortBy === k ? 'var(--paper)' : 'transparent',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              color: sortBy === k ? 'var(--brand-700)' : 'var(--ink-600)',
            }}>
              {k === 'score' ? 'Por cobertura' : 'Por nome'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Cobertura completa', value: rows.filter(r => r.score === COVERAGE_FIELDS.length).length, color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Com avisos', value: rows.filter(r => r.warns > 0 && r.score > 0).length, color: '#d97706', bg: '#fffbeb' },
          { label: 'Dados ausentes', value: rows.filter(r => r.statuses.includes('missing')).length, color: '#dc2626', bg: '#fef2f2' },
          { label: 'Campos monitorados', value: COVERAGE_FIELDS.length, color: 'var(--ink-700)', bg: 'var(--ink-50)' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}22`, borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 10, color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--ink-200)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--ink-600)', position: 'sticky', left: 0, background: 'var(--ink-50)' }}>Empresa</th>
              <th style={{ padding: '10px 10px', fontSize: 11, fontWeight: 700, color: 'var(--ink-600)', textAlign: 'center', minWidth: 60 }}>Score</th>
              {COVERAGE_FIELDS.map(f => (
                <th key={f.key} title={f.hint} style={{ padding: '10px 8px', fontSize: 10, fontWeight: 700, color: 'var(--ink-600)', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'help' }}>
                  {f.label}
                </th>
              ))}
            </tr>
            {/* Field totals row */}
            <tr style={{ background: 'var(--ink-100)', borderTop: '1px solid var(--ink-200)' }}>
              <td style={{ padding: '6px 14px', fontSize: 10, color: 'var(--ink-500)', fontWeight: 600, position: 'sticky', left: 0, background: 'var(--ink-100)' }}>Total empresas</td>
              <td />
              {totals.map((t, i) => (
                <td key={i} style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <span style={{ fontSize: 9, color: t.missing > 0 ? '#dc2626' : t.warn > 0 ? '#d97706' : '#16a34a', fontWeight: 700 }}>
                    {t.ok}✓ {t.warn > 0 ? `${t.warn}!` : ''} {t.missing > 0 ? `${t.missing}✗` : ''}
                  </span>
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ c, statuses, score }) => {
              const pct = Math.round(score / COVERAGE_FIELDS.length * 100)
              return (
                <tr key={c.companyKey} style={{ borderTop: '1px solid var(--ink-100)' }}>
                  <td style={{ padding: '8px 14px', fontWeight: 600, position: 'sticky', left: 0, background: 'var(--paper)' }}>{c.companyTitle}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                      background: pct >= 90 ? '#f0fdf4' : pct >= 60 ? '#fffbeb' : '#fef2f2',
                      color: pct >= 90 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626',
                    }}>{pct}%</span>
                  </td>
                  {statuses.map((s, i) => (
                    <CoverageCell key={i} status={s} hint={COVERAGE_FIELDS[i].hint} />
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-400)' }}>
        Passe o mouse sobre os cabeçalhos ou células para ver o detalhe de cada campo. ✓ ok · ! aviso · ✗ ausente/crítico
      </div>
    </div>
  )
}

// ─── MODULE: Distribuições Estatísticas ──────────────────────────────────────

interface MetricStatsDef {
  key: 'roi' | 'revenue' | 'influencedPercent' | 'cost' | 'conversionRate'
  companyField: keyof CompanyQualityData
  label: string
  fmt: (v: number) => string
  color: string
}

const DIST_METRICS: MetricStatsDef[] = [
  { key: 'roi',              companyField: 'roi',              label: 'ROI Widde',      fmt: v => `${formatRoiValue(v)}×`,  color: '#7c3aed' },
  { key: 'revenue',          companyField: 'totalRevenue',     label: 'Receita Total',  fmt: v => formatCurrency(v),   color: '#0ea5e9' },
  { key: 'influencedPercent',companyField: 'influencedPercent',label: '% Influenciada', fmt: v => `${v.toFixed(1)}%`,  color: '#10b981' },
  { key: 'cost',             companyField: 'proportionalCost', label: 'Custo Mensal',   fmt: v => formatCurrency(v),   color: '#f59e0b' },
  { key: 'conversionRate',   companyField: 'conversionRate',   label: 'Conversão %',    fmt: v => `${v.toFixed(2)}%`,  color: '#ec4899' },
]

function BoxPlot({ q1, median, q3, lowerFence, upperFence, min, max, mean, color, fmt, outliers }: {
  q1: number; median: number; q3: number
  lowerFence: number; upperFence: number
  min: number; max: number; mean: number
  color: string
  fmt: (v: number) => string
  outliers: number[]
}) {
  const W = 480, H = 60
  const lo = Math.min(lowerFence, min), hi = Math.max(upperFence, max)
  const range = hi - lo || 1
  const px = (v: number) => 40 + ((Math.max(lo, Math.min(hi, v)) - lo) / range) * (W - 80)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }}>
      {/* Fence whisker line */}
      <line x1={px(lowerFence)} x2={px(upperFence)} y1={H / 2} y2={H / 2} stroke="var(--ink-300)" strokeWidth={1.5} />
      {/* Whisker caps */}
      <line x1={px(lowerFence)} x2={px(lowerFence)} y1={H / 2 - 6} y2={H / 2 + 6} stroke="var(--ink-400)" strokeWidth={1.5} />
      <line x1={px(upperFence)} x2={px(upperFence)} y1={H / 2 - 6} y2={H / 2 + 6} stroke="var(--ink-400)" strokeWidth={1.5} />
      {/* IQR box */}
      <rect x={px(q1)} y={H / 2 - 12} width={Math.max(2, px(q3) - px(q1))} height={24}
        fill={color} fillOpacity={0.15} stroke={color} strokeWidth={1.5} rx={3} />
      {/* Median line */}
      <line x1={px(median)} x2={px(median)} y1={H / 2 - 13} y2={H / 2 + 13} stroke={color} strokeWidth={2.5} />
      {/* Mean diamond */}
      <polygon
        points={`${px(mean)},${H / 2 - 6} ${px(mean) + 5},${H / 2} ${px(mean)},${H / 2 + 6} ${px(mean) - 5},${H / 2}`}
        fill={color} fillOpacity={0.7}
      />
      {/* Outlier dots */}
      {outliers.map((v, i) => (
        <circle key={i} cx={px(v)} cy={H / 2} r={4} fill={color} fillOpacity={0.5} stroke={color} strokeWidth={1} />
      ))}
      {/* Axis labels */}
      <text x={px(lowerFence)} y={H - 4} fontSize="8" fill="var(--ink-400)" textAnchor="middle">{fmt(lowerFence)}</text>
      <text x={px(median)}     y={H - 4} fontSize="8" fill={color}            textAnchor="middle" fontWeight="700">{fmt(median)}</text>
      <text x={px(upperFence)} y={H - 4} fontSize="8" fill="var(--ink-400)" textAnchor="middle">{fmt(upperFence)}</text>
      {/* Legend */}
      <text x={4} y={H / 2 - 16} fontSize="8" fill="var(--ink-400)">p25</text>
      <text x={px(q3) + 4} y={H / 2 - 16} fontSize="8" fill="var(--ink-400)">p75</text>
    </svg>
  )
}

function DistributionsModule({ companies, report }: { companies: CompanyQualityData[]; report: DataQualityReport }) {
  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Distribuições Estatísticas</div>
        <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>
          Box plots para as principais métricas · linha = mediana · ◆ = média · pontos = outliers
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {DIST_METRICS.map(m => {
          const stats = report.statistics[m.key]
          if (!stats) return null
          const companyVals = companies.map(c => c[m.companyField] as number).filter(v => v != null && isFinite(v))
          const outlierVals = companyVals.filter(v => v < stats.lowerFence || v > stats.upperFence)

          return (
            <div key={m.key} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 10, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 13 }}>{m.label}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-500)' }}>
                  {companies.length} empresas
                  {outlierVals.length > 0 && (
                    <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}>
                      · {outlierVals.length} outlier{outlierVals.length > 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </div>
              <BoxPlot
                q1={stats.q1} median={stats.median} q3={stats.q3}
                lowerFence={stats.lowerFence} upperFence={stats.upperFence}
                min={stats.min} max={stats.max} mean={stats.mean}
                color={m.color} fmt={m.fmt}
                outliers={outlierVals}
              />
              {/* Stats summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 6, marginTop: 12 }}>
                {[
                  { label: 'Mín', value: m.fmt(stats.min) },
                  { label: 'p25', value: m.fmt(stats.q1) },
                  { label: 'Mediana', value: m.fmt(stats.median) },
                  { label: 'Média', value: m.fmt(stats.mean) },
                  { label: 'p75', value: m.fmt(stats.q3) },
                  { label: 'Máx', value: m.fmt(stats.max) },
                ].map(s => (
                  <div key={s.label} style={{ background: 'var(--ink-50)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: 'var(--ink-500)', textTransform: 'uppercase', fontWeight: 700 }}>{s.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-800)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── MODULE: Health Score ─────────────────────────────────────────────────────

interface HealthCheck {
  label: string
  points: number
  pass: (c: CompanyQualityData) => boolean
  hint: string
}

const HEALTH_CHECKS: HealthCheck[] = [
  { label: 'Tem pedidos',           points: 15, pass: c => c.totalOrders > 0,                          hint: 'Empresa processada sem nenhum pedido no período' },
  { label: 'Tem custo',             points: 10, pass: c => c.proportionalCost > 0,                     hint: 'Custo proporcional zerado — ROI não calculável' },
  { label: 'ROI calculado',         points: 10, pass: c => c.roi !== null,                             hint: 'ROI não pôde ser calculado' },
  { label: 'ROI ajustado',          points: 5,  pass: c => c.roiAdjusted != null,                      hint: 'ROI bayesiano indisponível' },
  { label: 'Vídeos ativos',         points: 10, pass: c => c.activeVideos > 0,                         hint: 'Nenhum vídeo ativo no período' },
  { label: 'Sessões com vídeo',     points: 10, pass: c => c.sessionsWithVideo > 0,                    hint: 'Nenhuma sessão com vídeo registrada no GA' },
  { label: 'Pedidos influenciados', points: 10, pass: c => c.influencedOrders > 0,                     hint: 'Nenhum pedido influenciado' },
  { label: '≥20 dias ativos GA',    points: 10, pass: c => (c.gaActiveDays ?? 0) >= 20,                hint: 'Poucos dias ativos compromete médias de sessão' },
  { label: 'Sem mês parcial',       points: 5,  pass: c => !c.isFirstMonthPartial,                     hint: 'Primeiro mês incompleto — dados de período reduzido' },
  { label: 'Sem anomalias',         points: 15, pass: c => c.anomalyFlags.length === 0,                hint: 'Empresa tem flags de anomalia detectadas' },
]

const MAX_SCORE = HEALTH_CHECKS.reduce((s, c) => s + c.points, 0)

function computeHealthScore(c: CompanyQualityData): number {
  return HEALTH_CHECKS.reduce((s, h) => s + (h.pass(c) ? h.points : 0), 0)
}

function HealthScoreBadge({ score }: { score: number }) {
  const pct = Math.round(score / MAX_SCORE * 100)
  const cfg = pct >= 80
    ? { bg: '#f0fdf4', color: '#16a34a', label: 'Bom' }
    : pct >= 55
    ? { bg: '#fffbeb', color: '#d97706', label: 'Atenção' }
    : { bg: '#fef2f2', color: '#dc2626', label: 'Crítico' }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: cfg.bg }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: cfg.color }}>{pct}</span>
      <span style={{ fontSize: 9, fontWeight: 700, color: cfg.color, textTransform: 'uppercase' }}>{cfg.label}</span>
    </span>
  )
}

function HealthScoreModule({ companies }: { companies: CompanyQualityData[] }) {
  const [expanded, setExpanded] = useState<string | null>(null)

  const rows = useMemo(() =>
    companies.map(c => ({ c, score: computeHealthScore(c), checks: HEALTH_CHECKS.map(h => h.pass(c)) }))
      .sort((a, b) => a.score - b.score),
    [companies]
  )

  const distribution = [
    { label: 'Crítico (< 55%)',  count: rows.filter(r => r.score / MAX_SCORE < 0.55).length,  color: '#dc2626', bg: '#fef2f2' },
    { label: 'Atenção (55–79%)', count: rows.filter(r => { const p = r.score / MAX_SCORE; return p >= 0.55 && p < 0.80 }).length, color: '#d97706', bg: '#fffbeb' },
    { label: 'Bom (≥ 80%)',      count: rows.filter(r => r.score / MAX_SCORE >= 0.80).length, color: '#16a34a', bg: '#f0fdf4' },
  ]

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Health Score por Empresa</div>
        <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>
          Score composto (0–{MAX_SCORE} pts) de confiabilidade dos dados · clique em uma empresa para ver o detalhe
        </div>
      </div>

      {/* Distribution summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
        {distribution.map(d => (
          <div key={d.label} style={{ background: d.bg, border: `1px solid ${d.color}33`, borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 10, color: d.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{d.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: d.color, marginTop: 2 }}>{d.count}</div>
            <div style={{ fontSize: 11, color: d.color, opacity: 0.7 }}>empresa{d.count !== 1 ? 's' : ''}</div>
          </div>
        ))}
      </div>

      {/* Score bar chart */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
        {rows.map(({ c, score, checks }) => {
          const pct = score / MAX_SCORE
          const color = pct >= 0.80 ? '#16a34a' : pct >= 0.55 ? '#d97706' : '#dc2626'
          const isOpen = expanded === c.companyKey
          return (
            <div key={c.companyKey} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 8, overflow: 'hidden' }}>
              <div
                onClick={() => setExpanded(isOpen ? null : c.companyKey)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer' }}
              >
                <div style={{ width: 130, flexShrink: 0, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.companyTitle}
                </div>
                <div style={{ flex: 1, height: 8, background: 'var(--ink-100)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct * 100}%`, background: color, borderRadius: 4, transition: 'width .3s' }} />
                </div>
                <HealthScoreBadge score={score} />
                <span style={{ fontSize: 10, color: 'var(--ink-400)', width: 16, textAlign: 'center' }}>{isOpen ? '▲' : '▼'}</span>
              </div>
              {isOpen && (
                <div style={{ borderTop: '1px solid var(--ink-100)', padding: '10px 14px', display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 4 }}>
                  {HEALTH_CHECKS.map((h, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                      <span style={{ color: checks[i] ? '#16a34a' : '#dc2626', fontWeight: 700, width: 12 }}>
                        {checks[i] ? '✓' : '✗'}
                      </span>
                      <span style={{ color: checks[i] ? 'var(--ink-700)' : 'var(--ink-500)' }}>{h.label}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ink-400)' }}>+{h.points}pts</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Checks legend */}
      <div style={{ background: 'var(--ink-50)', borderRadius: 8, padding: '10px 14px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 8 }}>Critérios de pontuação</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 4 }}>
          {HEALTH_CHECKS.map(h => (
            <div key={h.label} style={{ fontSize: 11, color: 'var(--ink-600)', display: 'flex', gap: 6 }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--brand-600)', fontWeight: 600, width: 26, flexShrink: 0 }}>+{h.points}</span>
              <span>{h.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── MODULE: Consistência de Atribuição ──────────────────────────────────────

interface ConsistencyCheck {
  id: string
  label: string
  description: string
  severity: 'critical' | 'warning'
  test: (c: CompanyQualityData) => boolean
}

const CONSISTENCY_CHECKS: ConsistencyCheck[] = [
  {
    id: 'rev_exceeds',
    label: 'Receita influenciada > receita total',
    description: 'influencedRevenue > totalRevenue — matematicamente impossível; indica bug de pipeline',
    severity: 'critical',
    test: c => c.influencedRevenue > c.totalRevenue && c.totalRevenue > 0,
  },
  {
    id: 'orders_exceeds',
    label: 'Pedidos influenciados > pedidos totais',
    description: 'influencedOrders > totalOrders — impossível; indica contagem dupla ou erro de atribuição',
    severity: 'critical',
    test: c => c.influencedOrders > c.totalOrders && c.totalOrders > 0,
  },
  {
    id: 'rev_no_orders',
    label: 'Receita sem pedidos',
    description: 'totalRevenue > 0 mas totalOrders = 0 — receita registrada sem pedidos correspondentes',
    severity: 'critical',
    test: c => c.totalRevenue > 0 && c.totalOrders === 0,
  },
  {
    id: 'high_influence',
    label: 'Taxa de influência muito alta (> 80%)',
    description: 'influencedPercent > 80% — estatisticamente improvável; pode indicar atribuição excessivamente ampla',
    severity: 'warning',
    test: c => c.influencedPercent > 80,
  },
  {
    id: 'videos_no_sessions',
    label: 'Vídeos ativos sem sessões registradas',
    description: 'activeVideos > 0 mas sessionsWithVideo = 0 — vídeos publicados mas GA não registrou sessões com vídeo',
    severity: 'warning',
    test: c => c.activeVideos > 0 && c.sessionsWithVideo === 0,
  },
  {
    id: 'influenced_no_sessions',
    label: 'Influência sem sessões com vídeo',
    description: 'influencedOrders > 0 mas sessionsWithVideo = 0 — pedidos atribuídos ao vídeo sem sessões de vídeo no GA',
    severity: 'warning',
    test: c => c.influencedOrders > 0 && c.sessionsWithVideo === 0,
  },
  {
    id: 'roi_no_cost',
    label: 'ROI calculado sem custo',
    description: 'roi ≠ null mas proportionalCost = 0 — ROI calculado com denominador zero é matematicamente inválido',
    severity: 'warning',
    test: c => c.roi !== null && c.proportionalCost === 0,
  },
  {
    id: 'bounce_anomaly',
    label: 'Bounce com vídeo maior que sem vídeo',
    description: 'bounceRateWithVideo > bounceRateWithoutVideo — usuários que veem vídeo deveriam ter menor bounce',
    severity: 'warning',
    test: c => c.bounceRateWithVideo > 0 && c.bounceRateWithoutVideo > 0 && c.bounceRateWithVideo > c.bounceRateWithoutVideo * 1.2,
  },
]

function ConsistencyModule({ companies }: { companies: CompanyQualityData[] }) {
  const results = useMemo(() =>
    CONSISTENCY_CHECKS.map(check => ({
      check,
      failing: companies.filter(c => check.test(c)),
    })),
    [companies]
  )

  const criticalCount = results.filter(r => r.check.severity === 'critical' && r.failing.length > 0).length
  const warningCount = results.filter(r => r.check.severity === 'warning' && r.failing.length > 0).length
  const allClear = criticalCount === 0 && warningCount === 0

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Consistência de Atribuição</div>
        <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>
          Validações cruzadas entre campos — detecta contradições internas nos dados
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
        <div style={{ background: allClear ? '#f0fdf4' : '#fef2f2', border: `1px solid ${allClear ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 10, color: allClear ? '#16a34a' : '#dc2626', fontWeight: 700, textTransform: 'uppercase' }}>Críticos</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: allClear ? '#16a34a' : '#dc2626', marginTop: 2 }}>{criticalCount}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>checks com falha</div>
        </div>
        <div style={{ background: warningCount === 0 ? '#f0fdf4' : '#fffbeb', border: `1px solid ${warningCount === 0 ? '#bbf7d0' : '#fde68a'}`, borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 10, color: warningCount === 0 ? '#16a34a' : '#d97706', fontWeight: 700, textTransform: 'uppercase' }}>Avisos</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: warningCount === 0 ? '#16a34a' : '#d97706', marginTop: 2 }}>{warningCount}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>checks com falha</div>
        </div>
        <div style={{ background: 'var(--ink-50)', border: '1px solid var(--ink-200)', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 10, color: 'var(--ink-600)', fontWeight: 700, textTransform: 'uppercase' }}>Checks OK</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink-800)', marginTop: 2 }}>
            {results.filter(r => r.failing.length === 0).length}/{CONSISTENCY_CHECKS.length}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>sem problemas</div>
        </div>
      </div>

      {allClear && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>Nenhuma inconsistência detectada nas {companies.length} empresas</div>
        </div>
      )}

      {/* Checks list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {results.map(({ check, failing }) => {
          const isCritical = check.severity === 'critical'
          const hasFail = failing.length > 0
          const borderColor = hasFail ? (isCritical ? '#fecaca' : '#fde68a') : '#d1fae5'
          const bgColor = hasFail ? (isCritical ? '#fef2f2' : '#fffbeb') : '#f0fdf4'
          const textColor = hasFail ? (isCritical ? '#991b1b' : '#92400e') : '#166534'

          return (
            <div key={check.id} style={{ border: `1px solid ${borderColor}`, background: bgColor, borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}>
                  {hasFail ? (isCritical ? '🚨' : '⚠️') : '✅'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: textColor }}>{check.label}</span>
                    {hasFail && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 999,
                        background: isCritical ? '#dc2626' : '#d97706', color: 'white',
                      }}>
                        {failing.length} empresa{failing.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: textColor, opacity: 0.8, lineHeight: 1.4 }}>{check.description}</div>
                  {hasFail && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {failing.map(c => (
                        <span key={c.companyKey} style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 999,
                          background: isCritical ? '#fecaca' : '#fde68a',
                          color: textColor, fontWeight: 600,
                        }}>
                          {c.companyTitle}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Props ────────────────────────────────────────────────────────────────────
export interface DataQualityPageProps {
  segmentResult: SegmentProcessResult | null
  productMode: 'stories' | 'live'
  startDate: string
  endDate: string
  selectedCompany: Company | null
  onStartProcess: (options: any) => Promise<{ sessionId: string }>
  onSessionStart: (sessionId: string) => void
  progress: ProcessProgress | null
  isConnected: boolean
  isFormValid: boolean
  forceCache: boolean
  setForceCache: (v: boolean) => void
  isStarting: boolean
}

// ─── Main page with sidebar ───────────────────────────────────────────────────
export function DataQualityPage(props: DataQualityPageProps) {
  const {
    segmentResult,
    productMode,
    startDate,
    endDate,
    selectedCompany,
    onStartProcess,
    onSessionStart,
    progress,
    isConnected,
    isFormValid,
    forceCache,
    setForceCache,
    isStarting
  } = props

  const { report, isLoading, error, analyzeQuality } = useDataQuality()
  const [activeModule, setActiveModule] = useState<ModuleKey>('quality')
  const [selectedCompanyKey, setSelectedCompanyKey] = useState<string | null>(
    null
  )

  // Filter companies to match backend's cleanCompanies:
  // eligibility (totalOrders > 0, proportionalCost > 0) + exclude auto-excluded by health checks
  const autoExcludeSet = new Set(segmentResult?.autoExcludeKeys ?? [])
  const filteredReportCompanies =
    report?.companies.filter(
      c => c.totalOrders > 0 && c.proportionalCost > 0 && !autoExcludeSet.has(c.companyKey)
    ) ?? []

  // Auto-analyze whenever segmentResult changes
  useEffect(() => {
    if (segmentResult && startDate && endDate) {
      analyzeQuality(segmentResult, startDate, endDate)
    }
  }, [segmentResult, startDate, endDate, analyzeQuality])

  const selectedCompanyDrillDown =
    report?.companies.find(c => c.companyKey === selectedCompanyKey) ?? null
  const hasData = !!report

  return (
    <div>
      {/* Search & Process form */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Processar Segmento para Análise</h2>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <ProcessButton
            onStartProcess={onStartProcess}
            onSessionStart={onSessionStart}
            progress={progress}
            isConnected={isConnected}
            disabled={!isFormValid}
            companyKey={selectedCompany?.key || ''}
            startDate={startDate}
            endDate={endDate}
            forceDownload={forceCache}
            forceProcess={forceCache}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              color: '#6b7280',
              cursor: 'pointer'
            }}
          >
            <input
              type="checkbox"
              checked={forceCache}
              onChange={e => setForceCache(e.target.checked)}
              style={{ width: 'auto', margin: 0 }}
            />
            Ignorar cache
          </label>
        </div>
        <ProcessStatus
          progress={progress}
          isConnected={isConnected}
          isStarting={isStarting}
        />
      </div>

      {/* Live Commerce warning */}
      {productMode === 'live' && (
        <div style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          padding: '12px 16px', marginBottom: 16,
          background: '#fff8ec', border: '1px solid #fde68a', borderRadius: 10,
        }}>
          <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>⚠</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 3 }}>
              Qualidade de Dados limitada no modo Live Commerce
            </div>
            <div style={{ fontSize: 12, color: '#b45309', lineHeight: 1.5 }}>
              A análise de qualidade foi projetada para processamentos de <strong>segmento completo</strong> (múltiplas empresas).
              No modo Live Commerce, o processamento é por empresa individual — anomalias estatísticas e comparações entre empresas não estarão disponíveis.
              Mude para <strong>Video Commerce</strong> e processe um segmento para ver a análise completa.
            </div>
          </div>
        </div>
      )}

      {/* Results header */}
      {segmentResult && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            Segmento: <strong>{segmentResult.segment}</strong> |{' '}
            {segmentResult.companies.length} empresas | {startDate} a {endDate}
            {isLoading && (
              <span style={{ marginLeft: 8, color: '#7c3aed' }}>
                Analisando...
              </span>
            )}
          </span>
        </div>
      )}

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: '8px 12px',
            background: '#fef2f2',
            borderRadius: 6,
            fontSize: 13,
            color: '#dc2626'
          }}
        >
          {error}
        </div>
      )}

      {/* Sidebar + Main content */}
      {hasData && (
        <div style={{ display: 'flex', gap: 0, minHeight: 500 }}>
          {/* Sidebar */}
          <div
            style={{
              width: 220,
              flexShrink: 0,
              background: '#f9fafb',
              borderRight: '1px solid #e5e7eb',
              borderRadius: '8px 0 0 8px',
              padding: '12px 0'
            }}
          >
            {MODULES.map(mod => (
              <button
                key={mod.key}
                onClick={() => setActiveModule(mod.key)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  background: activeModule === mod.key ? '#fff' : 'transparent',
                  borderLeft:
                    activeModule === mod.key
                      ? '3px solid #7c3aed'
                      : '3px solid transparent',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={e => {
                  if (activeModule !== mod.key)
                    e.currentTarget.style.background = '#f3f4f6'
                }}
                onMouseLeave={e => {
                  if (activeModule !== mod.key)
                    e.currentTarget.style.background = 'transparent'
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }}>{mod.icon}</span>
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: activeModule === mod.key ? 600 : 500,
                      color: activeModule === mod.key ? '#7c3aed' : '#374151'
                    }}
                  >
                    {mod.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                    {mod.description}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Main content area */}
          <div
            style={{
              flex: 1,
              padding: 20,
              background: '#fff',
              borderRadius: '0 8px 8px 0',
              border: '1px solid #e5e7eb',
              borderLeft: 'none',
              overflow: 'auto'
            }}
          >
            {activeModule === 'quality' && (
              <QualityModule
                report={report!}
                onSelectCompany={setSelectedCompanyKey}
              />
            )}
            {activeModule === 'coverage' && (
              <CoverageModule companies={report!.companies} />
            )}
            {activeModule === 'health-score' && (
              <HealthScoreModule companies={filteredReportCompanies} />
            )}
            {activeModule === 'consistency' && (
              <ConsistencyModule companies={report!.companies} />
            )}
            {activeModule === 'distributions' && (
              <DistributionsModule companies={filteredReportCompanies} report={report!} />
            )}
            {activeModule === 'roi-methods' && (
              <RoiMethodsModule companies={filteredReportCompanies} backendAvgRoi={segmentResult?.roi.avgRoi ?? null} />
            )}
          </div>
        </div>
      )}

      {/* Drill-down panel */}
      {selectedCompanyDrillDown && report && (
        <>
          <div
            onClick={() => setSelectedCompanyKey(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 999
            }}
          />
          <DrillDownPanel
            company={selectedCompanyDrillDown}
            anomalies={report.anomalies}
            stats={report.statistics}
            onClose={() => setSelectedCompanyKey(null)}
          />
        </>
      )}
    </div>
  )
}
